import type { VirtualElement } from '@popperjs/core';

import { useState, useCallback } from 'react';
import { usePopper } from 'react-popper';
import * as Sentry from '@sentry/react';

import { extractTextWithContext, SelectedTextContext } from '../helpers/selected-text';

type TextPosition = {
  text: string;
  virtualReference: VirtualElement;
  context: string;
};

export const useTextSelectionToAskEcho = (htmlDivElement: HTMLDivElement | null) => {
  const [selectedTextState, setSelectedTextState] = useState<TextPosition | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    selectedTextState?.virtualReference,
    popperElement,
    {
      modifiers: [{ name: 'offset', options: { offset: [0, 40] } }],
      placement: 'top',
    },
  );

  const clearSelection = useCallback(() => {
    setSelectedTextState(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const onSelectStart = useCallback(() => {
    setSelectedTextState(null);
  }, []);

  const onSelectEnd = useCallback(() => {
    const selection = window.getSelection();

    if (!selection || selection.isCollapsed) {
      clearSelection();
      return;
    }

    if (!htmlDivElement) {
      clearSelection();
      return;
    }

    if (!selection.rangeCount) {
      return;
    }

    let range: Range | null = null;
    let context: SelectedTextContext | null = null;

    try {
      range = selection.getRangeAt(0);
      context = extractTextWithContext(htmlDivElement, range);
      if (!context) return;
    } catch (error) {
      Sentry.captureException(error, {
        contexts: {
          useTextSelectionToAskEcho: {
            range,
            selection,
          },
        },
      });
      return;
    }

    const { selectedText, fullContext } = context;

    const virtualReference: VirtualElement = {
      getBoundingClientRect: () => {
        if (!range) {
          return {
            width: 0,
            height: 0,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            x: 0,
            y: 0,
            toJSON: () => {},
          };
        }
        return range.getBoundingClientRect();
      },
    };

    setSelectedTextState({
      text: selectedText,
      virtualReference,
      context: fullContext,
    });
  }, [clearSelection, htmlDivElement]);

  const onSelectedTextClick = useCallback(
    (e: MouseEvent) => {
      // context menu is opened when right-clicking
      if (e.button === 2) return;

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        clearSelection();
        return;
      }

      try {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
          clearSelection();
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [clearSelection],
  );

  return {
    selectedText: selectedTextState,
    setPopperElement,
    popperStyles,
    popperAttributes,
    onSelectEnd,
    onSelectStart,
    onSelectedTextClick,
    clearSelection,
  };
};
